<template>
  <div class="plant-entry-row">
    <div class="plant-entry-select-wrap">
      <b-form-select
        :value="value.plant_equipment_id"
        :options="selectOptions"
        :disabled="disabled"
        size="sm"
        class="plant-entry-select"
        @change="update('plant_equipment_id', $event)"
      />
    </div>
    <div class="plant-entry-qty-wrap">
      <span class="plant-entry-qty-label">{{ quantityLabel }}</span>
      <b-form-input
        :value="value.quantity"
        type="number"
        :min="minQuantity"
        :max="maxQuantity"
        step="0.1"
        size="sm"
        :disabled="disabled"
        class="plant-entry-qty"
        :placeholder="String(minQuantity)"
        @update="update('quantity', $event === '' ? null : Number($event))"
        @change="onQuantityChange"
      />
    </div>
    <button
      type="button"
      class="plant-entry-remove"
      :disabled="disabled"
      aria-label="Remove equipment"
      @click="$emit('remove')"
    >
      &times;
    </button>
  </div>
</template>

<script>
import {
  PLANT_QUANTITY_MAX,
  clampPlantQuantity,
} from '@/utils/timesheetHelpers'

export default {
  name: 'PlantEntryRow',
  props: {
    // { plant_equipment_id: number|null, quantity: number|null }
    value: { type: Object, required: true },
    equipmentOptions: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    // Smallest amount allowed; a typed lower or blank value snaps up to it.
    minQuantity: { type: Number, default: 0 },
    // Items already picked on other rows, so each item can only be used once.
    takenIds: { type: Array, default: () => [] },
  },
  computed: {
    maxQuantity() {
      return PLANT_QUANTITY_MAX
    },

    selectOptions() {
      return this.equipmentOptions.map((o) =>
        o.value !== null &&
        o.value !== this.value.plant_equipment_id &&
        this.takenIds.includes(o.value)
          ? { ...o, text: `${o.text} (already added)`, disabled: true }
          : o,
      )
    },

    // The amount is time used, so name the unit the chosen item is logged in.
    quantityLabel() {
      const option = this.equipmentOptions.find(
        (o) => o.value === this.value.plant_equipment_id,
      )
      const rateType = option && option.rateType
      if (rateType === 'hourly') return 'Hours used'
      if (rateType === 'day') return 'Days used'
      return 'Hours / days'
    },
  },
  methods: {
    update(key, val) {
      this.$emit('input', { ...this.value, [key]: val })
    },

    // Runs when the box loses focus, so typing "0.5" on the way to "1.5" isn't
    // snapped up mid-keystroke.
    onQuantityChange(raw) {
      this.update('quantity', clampPlantQuantity(raw, this.minQuantity))
    },
  },
}
</script>

<style lang="scss" scoped>
// The label sits above the amount box outside the row's layout, so the
// dropdown, the box and the × all centre on the same line.
// Fixed columns: the dropdown takes the spare width, so the amount box and the
// × always sit together at the end of the row, 8px apart.
.plant-entry-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 84px 26px;
  align-items: center;
  column-gap: 8px;
  padding-top: 18px;
  margin: 0 0 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.plant-entry-select-wrap {
  min-width: 0;
}

.plant-entry-select {
  width: 100%;
}

.plant-entry-qty-wrap {
  position: relative;
}

.plant-entry-qty-label {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  margin-bottom: 2px;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1.4;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  text-align: center;
  white-space: nowrap;
}

.plant-entry-qty {
  width: 100%;
  text-align: center;
}

.plant-entry-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 31px;
  border: 0;
  background: transparent;
  color: #adb5bd;
  font-size: 16px;
  line-height: 1;
  border-radius: 4px;

  &:hover:not(:disabled) {
    color: #dc3545;
    background: #fdecee;
  }

  &:disabled {
    opacity: 0.5;
  }
}
</style>
